/**
 * Content-only variant of the documents step for use in the unified
 * create collection modal (no header/footer chrome).
 */
import { Button } from '@heidi/design-system/button';
import { FileDropzone, type FileUploadRejection } from '@heidi/design-system/file-upload';
import { TypographyCaption, TypographyP2 } from '@heidi/design-system/typography';
import type { TranslationKey } from '@heidi/runtime/i18n';
import { useTranslations } from '@heidi/runtime/i18n';
import { ArrowUpToLine, Plus } from 'lucide-react';
import { useRef } from 'react';
import { USER_DOCUMENTS_ACCEPT_FORMATS } from '../../../contracts';
import { getFileIcon } from '../DocumentMetadataPopover.utils';
import { LibraryUploadRiskOptin } from '../LibraryUploadRiskOptin';
import { createLibraryUploadFile, type LibraryUploadFile } from '../upload.types';
import { useLibraryRiskOptinGate } from '../useLibraryRiskOptinGate';
import { useLibraryUploadRiskAcceptance } from '../useLibraryUploadRiskAcceptance';
import { DocumentSearchInput, type SelectedExistingDoc } from './DocumentSearchInput';
import { RemovableDocRow } from './RemovableDocRow';

export interface DocumentsStepContentProps {
  stagedFiles: LibraryUploadFile[];
  onAddFiles: (files: LibraryUploadFile[]) => void;
  onFilesRejected: (rejections: FileUploadRejection[]) => void;
  onRemoveFile: (uid: string) => void;
  selectedExistingDocs: SelectedExistingDoc[];
  onAddExistingDoc: (doc: SelectedExistingDoc) => void;
  onRemoveExistingDoc: (id: string) => void;
}

export const DocumentsStepContent = ({
  stagedFiles,
  onAddFiles,
  onFilesRejected,
  onRemoveFile,
  selectedExistingDocs,
  onAddExistingDoc,
  onRemoveExistingDoc,
}: DocumentsStepContentProps) => {
  const { t } = useTranslations();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const totalDocuments = stagedFiles.length + selectedExistingDocs.length;
  const { hasAccepted, accept, isPending } = useLibraryUploadRiskAcceptance();
  const riskGate = useLibraryRiskOptinGate({
    hasAccepted,
    accept,
    isPending,
  });

  const handleFilesAccepted = (files: File[]) => {
    onAddFiles(files.map(createLibraryUploadFile));
  };

  const handleFileInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const fileList = e.target.files;
    if (fileList && fileList.length > 0) {
      onAddFiles(Array.from(fileList).map(createLibraryUploadFile));
    }
    e.target.value = '';
  };

  if (riskGate.needsOptin) {
    return (
      <div className="flex min-h-0 flex-1 flex-col overflow-y-auto">
        <LibraryUploadRiskOptin
          accepted={riskGate.checkboxAccepted}
          onAcceptedChange={riskGate.setCheckboxAccepted}
          onConfirm={riskGate.confirm}
        />
      </div>
    );
  }

  return (
    <div className="flex min-h-0 flex-1 flex-col gap-3">
      <div className="flex shrink-0 items-center gap-2">
        <DocumentSearchInput
          excludedDocumentIds={selectedExistingDocs.map((d) => d.id)}
          onSelectDocument={onAddExistingDoc}
          placeholder={t('library.wizard.documents.searchPlaceholder' as TranslationKey)}
          className="min-w-0 flex-1"
        />
        <Button
          type="button"
          variant="secondary"
          size="md"
          className="shrink-0"
          onClick={() => fileInputRef.current?.click()}
        >
          <Plus />
          {t('library.wizard.documents.addButton' as TranslationKey)}
        </Button>
      </div>
      <div className="flex min-h-0 flex-1 flex-col">
        {totalDocuments === 0 ? (
          <FileDropzone
            multiple
            accept={USER_DOCUMENTS_ACCEPT_FORMATS}
            onFilesAccepted={handleFilesAccepted}
            onFilesRejected={onFilesRejected}
            className="h-full flex-1"
          >
            <div className="flex h-full flex-col items-center justify-center">
              <div className="mb-3 flex size-12 items-center justify-center rounded-full bg-background-secondary">
                <ArrowUpToLine className="text-text-primary" size={24} />
              </div>
              <TypographyP2 className="font-medium text-text-primary">
                {t('library.wizard.entry.dragDropLabel' as TranslationKey)}
              </TypographyP2>
              <TypographyCaption className="mt-1 text-text-tertiary">
                {t('library.wizard.entry.supportedFormats' as TranslationKey)}
              </TypographyCaption>
            </div>
          </FileDropzone>
        ) : (
          <div className="overflow-y-auto">
            {selectedExistingDocs.map((doc) => (
              <RemovableDocRow
                key={doc.id}
                icon={getFileIcon(doc.filename)}
                label={doc.filename}
                onRemove={() => onRemoveExistingDoc(doc.id)}
                removeLabel={t('library.wizard.documents.removeLabel' as TranslationKey)}
              />
            ))}
            {stagedFiles.map((file) => (
              <RemovableDocRow
                key={file.uid}
                icon={getFileIcon(file.name)}
                label={file.name}
                onRemove={() => onRemoveFile(file.uid)}
                removeLabel={t(
                  'library.wizard.documents.removeFileLabel' as TranslationKey
                )}
              />
            ))}
          </div>
        )}
      </div>
      <input
        ref={fileInputRef}
        type="file"
        multiple
        accept={USER_DOCUMENTS_ACCEPT_FORMATS}
        onChange={handleFileInputChange}
        className="hidden"
      />
    </div>
  );
};
